<template>
  <StandardCard
    :border="false"
    card-text-class="pa-0"
  >
    <v-stepper-vertical
      @update:model-value="onNestedTaskStepperModelUpdate"
      :disabled="locked"
      :editable="false"
      :model-value="nestedTaskStepperModel"
      class="license-workflow-task-stepper"
      flat
      hide-actions
      multiple
      non-linear
    >
      <v-stepper-vertical-item
        v-for="(task, taskIndex) in phase.tasks ?? []"
        :key="task.id"
        :color="task.status === 'completed' ? 'success' : 'light-grey'"
        :complete="task.status === 'completed'"
        :edit-icon="null"
        :icon="stepperTaskIcon(task)"
        :title="task.name"
        :value="taskIndex + 1"
        complete-icon="check"
      >
        <template #title>
          <div class="license-workflow-task-title-row d-flex align-center ga-3 w-100 min-w-0">
            <button
              @click="onTaskTitleClick(task)"
              :aria-label="taskTitleAriaLabel(task)"
              :class="[
                'license-workflow-task-title-button text-left min-w-0 flex-grow-1',
                { 'license-workflow-task-title-button--clickable': !locked },
              ]"
              :disabled="locked"
              type="button"
            >
              <div class="text-body-1 font-weight-medium text-high-emphasis text-wrap">
                {{ task.name }}
              </div>

              <div
                v-if="phaseTaskDetails[taskIndex]?.subtitle"
                class="text-body-1 text-medium-emphasis text-wrap mt-1"
              >
                {{ phaseTaskDetails[taskIndex].subtitle }}
              </div>

              <div
                v-if="phaseTaskDetails[taskIndex]?.chips.length"
                class="d-flex flex-wrap align-center ga-1 mt-1"
              >
                <StandardChip
                  v-for="chip in phaseTaskDetails[taskIndex].chips"
                  :key="chip.key"
                  :color="chip.color"
                  :prepend-icon="chip.prependIcon"
                  :role="chip.role"
                  :text="chip.text"
                  :text-translate="false"
                  size="small"
                  variant="tonal"
                />
              </div>
            </button>

            <div
              v-if="isSpecialist && !locked && isAssignableOrReviewable(task)"
              class="d-flex ga-2 flex-shrink-0 align-center"
            >
              <StandardButton
                v-if="task.status !== 'completed' && task.status !== 'skipped'"
                @click="emit('assignTask', task)"
                aria-label="Assign"
                icon="person_add"
                role="secondary"
                size="small"
              />
            </div>
          </div>
        </template>

        <div
          v-if="task.description"
          class="text-caption text-medium-emphasis text-wrap"
        >
          {{ task.description }}
        </div>
      </v-stepper-vertical-item>
    </v-stepper-vertical>
  </StandardCard>
</template>

<script setup>
import { capitalize } from '@/plugins/filters';
import { statusColorNameForKey } from '@/shared/composables/useStatusChipColors';
import useDateFormatting from '@/shared/composables/useDateFormatting';
import useRole from '@/shared/composables/useRole';
import { workflowTaskStatusLabel } from '@/shared/assets/constants';

const props = defineProps({
  locked: {
    type: Boolean,
    required: true,
  },
  phase: {
    type: Object,
    required: true,
  },
  taskExpandedIndices: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:taskExpandedIndices', 'assignTask', 'reviewTask']);

const $bct = inject('translator');

const { isSpecialist } = useRole();
const { formatShortDate } = useDateFormatting();

const phaseTaskDetails = computed(() => {
  const tasks = props.phase.tasks ?? [];

  return tasks.map((task) => ({
    subtitle: taskSubtitle(task),
    chips: taskShowsInfoChips(task) ? buildTaskInfoChips(task) : [],
  }));
});

const nestedTaskStepperModel = computed(() => {
  if (props.locked) return [];

  return props.taskExpandedIndices ?? [];
});

function isAssignableOrReviewable(task) {
  return task.review_required || task.assignable_to != 'provider';
}

function onNestedTaskStepperModelUpdate() {
  if (props.locked) return;

  const fullIndices = (props.phase.tasks ?? []).map((_task, taskIndex) => taskIndex + 1);

  emit('update:taskExpandedIndices', fullIndices);
}

function onTaskTitleClick(task) {
  if (props.locked) return;

  emit('reviewTask', task);
}

function taskTitleAriaLabel(task) {
  if (props.locked) return `${task.name} (phase locked)`;

  return `Review ${task.name}`;
}

function stepperTaskIcon(task) {
  if (props.locked && task.status !== 'completed') return 'lock';

  return taskIcon(task.status);
}

function taskIcon(status) {
  const icons = {
    completed: 'check_circle',
    in_review: 'rate_review',
    open: 'radio_button_unchecked',
    pending: 'lock',
    skipped: 'skip_next',
  };

  return icons[status] || 'help';
}

function memberDisplayName(member) {
  if (!member) return null;
  if (typeof member === 'string') return member;

  return member.name ?? null;
}

function taskAssigneeDisplayName(task) {
  return memberDisplayName(task.assignee) || task.assignee_name || null;
}

function taskReviewerDisplayName(task) {
  return memberDisplayName(task.review_assignee) || null;
}

function taskSubmissionSubtitle(task) {
  if (task.submission_status !== 'submitted' || !task.submitted_at) return null;

  const reviewerName = task.reviewer_name;
  const submitterName = task.submitted_by_name;
  if (!reviewerName || !submitterName) return null;

  return (
    `${$bct('Submitted')} ${formatShortDate(task.submitted_at)} ${$bct('by')} ${submitterName}` +
    ` · ${$bct('Assigned to')} ${reviewerName}`
  );
}

function taskSubtitle(task) {
  const submissionSubtitle = taskSubmissionSubtitle(task);
  if (submissionSubtitle) return submissionSubtitle;

  if (Object.hasOwn(task, 'subtitle')) return task.subtitle === '' ? null : task.subtitle;

  return null;
}

function taskShowsInfoChips(task) {
  if (taskSubmissionSubtitle(task)) return false;
  if (Object.hasOwn(task, 'subtitle')) return false;

  return true;
}

function taskStatusChipText(task) {
  if (task.status === 'completed') {
    const datePart = task.completed_at ? formatShortDate(task.completed_at) : null;

    if (datePart) return `${$bct('Approved')} ${datePart}`;

    return $bct('Approved');
  }

  return capitalize($bct(workflowTaskStatusLabel(task.status) ?? 'task'));
}

function taskStatusChipColor(task) {
  if (task.status === 'completed') return statusColorNameForKey('approved');

  return statusColorNameForKey(task.status);
}

function buildTaskInfoChips(task) {
  const chips = [
    {
      key: 'status',
      text: taskStatusChipText(task),
      color: taskStatusChipColor(task),
      role: 'status',
      prependIcon: undefined,
    },
  ];

  if (task.assignable_to !== 'provider') {
    const assigneeName = taskAssigneeDisplayName(task);
    chips.push({
      key: 'assignee',
      text: `${$bct('Assignee')}: ${assigneeName ?? $bct('None')}`,
      color: undefined,
      role: undefined,
      prependIcon: 'person',
    });
  }

  if (task.review_required) {
    const reviewerName = taskReviewerDisplayName(task);
    chips.push({
      key: 'reviewer',
      text: `${$bct('Reviewer')}: ${reviewerName ?? $bct('None')}`,
      color: undefined,
      role: undefined,
      prependIcon: 'fact_check',
    });
  }

  if (task.workflow_artifact?.artifact_type) {
    chips.push({
      key: 'artifact',
      text: `${task.workflow_artifact.artifact_type} artifact`,
      color: undefined,
      role: undefined,
      prependIcon: 'description',
    });
  }

  return chips;
}
</script>

<style scoped>
.license-workflow-task-stepper :deep(.v-expansion-panel-title.v-expansion-panel-title--static) {
  padding: 0;
}

.license-workflow-task-stepper :deep(.v-stepper-vertical-item:not(:last-child)::before) {
  left: 11px;
  top: 39px !important;
}

.license-workflow-task-stepper :deep(.v-stepper-vertical-item__avatar.v-avatar) {
  align-items: center;
  display: flex;
  justify-content: center;
}

.license-workflow-task-stepper :deep(.v-expansion-panel-title__icon) {
  display: none;
}

.license-workflow-task-stepper
  :deep(
    .v-stepper-vertical-item:not(.v-stepper-vertical-item--editable)
      .license-workflow-task-title-row
  ) {
  pointer-events: auto;
  position: relative;
  z-index: 1;
}

.license-workflow-task-title-button {
  background: transparent;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  transition: background-color 0.15s ease-in-out;
}

.license-workflow-task-title-button:disabled {
  cursor: default;
}

.license-workflow-task-title-button--clickable {
  cursor: pointer;
}

.license-workflow-task-title-button--clickable:hover,
.license-workflow-task-title-button--clickable:focus-visible {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.license-workflow-task-title-button:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
</style>
